import { Controller } from "@hotwired/stimulus";
import { defaultTo, escape, flatten, last, partition } from "lodash-es";
import i18n from "../lib/i18n";

export default class extends Controller {
  static targets = ["maps"];
  static values = {
    facilities: Array,
    params: Object,
    displayMode: { type: String, default: "energy_class" },
  };

  initialize() {
    this.activeWindow = null;
    this.markers = [];
  }

  async initMap() {
    const { Map } = await google.maps.importLibrary("maps");

    const mapOptions = {
      zoom: 6,
      center: new google.maps.LatLng(57.7041, 11.9649),
      mapId: "7af3aca158640605",
    };

    this.map = new Map(this.mapsTarget, mapOptions);

    const bounds = new google.maps.LatLngBounds();
    this.facilitiesValue.forEach((facility) => {
      bounds.extend(new google.maps.LatLng(facility.lat, facility.long));
      this.createMarker(facility);
    });

    const MAX_ZOOM = 17;
    if (this.facilitiesValue.length > 0) {
      google.maps.event.addListenerOnce(this.map, "bounds_changed", () => {
        this.map.setZoom(Math.min(this.map.getZoom(), MAX_ZOOM));
      });
      this.map.fitBounds(bounds);
    }
  }

  async createMarker(facilityData) {
    const { AdvancedMarkerElement } = await google.maps.importLibrary("marker");

    const planimaFlagImg = document.createElement("img");

    let infoWindowContent;

    if (this.displayModeValue === "climate_risk") {
      ({ src: planimaFlagImg.src, infoWindowContent } =
        this._buildClimateRiskMarkerContent(facilityData));
    } else {
      ({ src: planimaFlagImg.src, infoWindowContent } =
        this._buildEnergyClassMarkerContent(facilityData));
    }

    const markerOptions = {
      position: new google.maps.LatLng(facilityData.lat, facilityData.long),
      map: this.map,
      title: facilityData.name,
      content: planimaFlagImg,
      gmpClickable: true,
    };

    const googleMarker = new AdvancedMarkerElement(markerOptions);
    this.markers.push(googleMarker);

    const infoWindow = new google.maps.InfoWindow({
      content: infoWindowContent,
    });

    googleMarker.addListener("click", () => {
      if (this.activeWindow) {
        this.activeWindow.close();
      }
      infoWindow.open(this.map, googleMarker);
      this.activeWindow = infoWindow;
    });
  }

  handleDisplayModeChange({ detail: { mode } }) {
    this.displayModeValue = mode;
  }

  displayModeValueChanged() {
    if (!this.map) return;
    this.markers.forEach((marker) => {
      marker.map = null;
    });
    this.markers = [];
    this.activeWindow?.close();
    this.activeWindow = null;
    this.initMap();
  }

  paramsValueChanged() {
    this.initMap();
  }

  _buildClimateRiskMarkerContent(facilityData) {
    const riskColors = {
      high: "red",
      medium: "yellow",
    };
    const pinColor = riskColors[facilityData.highest_risk_level] || "gray";
    const src = `/pin-planima-${pinColor}_60x77px.png`;

    const highRiskNames = facilityData.high_risk_names || [];
    const riskSection =
      highRiskNames.length > 0
        ? `<p>
              <strong class="inline-block mb-8">${escape(i18n.t("js.stimulus_controllers.google_maps.highest_risks"))}</strong><br />
              ${highRiskNames.map((name) => `<span class="label label-${pinColor}">${escape(name)}</span>`).join(" ")}
            </p>`
        : "";

    return {
      src,
      infoWindowContent: this._buildInfoWindow(facilityData, riskSection),
    };
  }

  _buildEnergyClassMarkerContent(facilityData) {
    const ENERGY_CLASS_ORDER = ["A0", "A", "B", "C", "D", "E", "F", "G"];
    const sortedEnergyClasses = [...facilityData.energy_classes].sort(
      (a, b) => {
        const idxA = ENERGY_CLASS_ORDER.indexOf(a);
        const idxB = ENERGY_CLASS_ORDER.indexOf(b);
        return (
          (idxA === -1 ? Infinity : idxA) - (idxB === -1 ? Infinity : idxB)
        );
      }
    );
    const worstEnergyClass = defaultTo(
      last(sortedEnergyClasses.filter((c) => c !== "0")),
      "0"
    );
    const colors = {
      A0: "green",
      A: "green",
      B: "light-green",
      C: "yellow-green",
      D: "yellow",
      E: "light-orange",
      F: "orange",
      G: "red",
      0: "gray",
    };
    const src = `/pin-planima-${colors[worstEnergyClass]}_60x77px.png`;

    const energyClassesWithZeroLast = flatten(
      partition(sortedEnergyClasses, (x) => x != "0")
    );

    const energyContent = `
        <p>
          <a href="/facilities/${facilityData.id}" class="energy-class-link">
          ${energyClassesWithZeroLast
            .map(
              (energyClass) =>
                `<span class="energy-class energy-class-${escape(energyClass)} fw-heavy">${energyClass !== "0" ? escape(energyClass) : ""}</span>`
            )
            .join(" ")}
          </a>
        </p>
      `;

    return {
      src,
      infoWindowContent: this._buildInfoWindow(facilityData, energyContent),
    };
  }

  _buildInfoWindow(facilityData, middleContent) {
    return `
      <div class="custom-info-window">
        <p class="mt-0">
          <a href="/facilities/${facilityData.id}">
            <strong>${escape(facilityData.name)}</strong>
          </a>
        </p>
        ${middleContent}
        <p>
          ${escape(facilityData.address)}<br />
          ${escape(facilityData.zip_code)} ${escape(facilityData.region)}
        </p>
      </div>
    `;
  }
}
